import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Field, Input, Textarea } from "@/components/ui/input";
import { CheckboxField } from "@/components/ui/menu";
import { NumberInput } from "@/components/ui/number-input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useUpcomingReminders } from "@/hooks/useSettings";
import { formatDate, money as formatMoney } from "@/lib/format";
import type { InvoiceSettings, ReminderKind } from "@/types";

/** Kept in step with DEFAULT_* in apps/server/src/jobs/payment-reminders.ts. */
const DEFAULT_BEFORE_DUE = `Hi {{billableName}},

A quick reminder that invoice {{invoiceNumber}} for {{totalAmount}} is due {{dueWhen}}.

If you have already paid, thank you, and please ignore this email.`;

const DEFAULT_OVERDUE = `Hi {{billableName}},

Invoice {{invoiceNumber}} was due on {{dueDate}} and is now {{daysOverdue}} days overdue. {{interestNote}}

If you have already sent payment, thank you, and please ignore this email.`;

export const REMINDER_LABEL: Record<ReminderKind, string> = {
  before_due: "Due soon",
  due: "Due today",
  overdue: "Overdue",
};

export function reminderWhen(offsetDays: number) {
  if (offsetDays === 0) return "On the due date";
  const n = Math.abs(offsetDays);
  return `${n} ${n === 1 ? "day" : "days"} ${offsetDays < 0 ? "before" : "after"} due`;
}

const parseDays = (text: string) =>
  [
    ...new Set(
      text
        .split(/[\s,]+/)
        .map((s) => Math.round(Number(s)))
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 365),
    ),
  ].sort((a, b) => a - b);

interface Props {
  draft: InvoiceSettings | null;
  loading: boolean;
  dirty: boolean;
  patch: (next: Partial<InvoiceSettings>) => void;
  footer?: ReactNode;
}

export function RemindersCard({ draft, loading, dirty, patch, footer }: Props) {
  const anyOn =
    !!draft &&
    (draft.reminderBeforeDueEnabled ||
      draft.reminderOnDueEnabled ||
      draft.overdueRemindersEnabled);
  const upcoming = useUpcomingReminders(anyOn);

  // The day list keeps its own text so "7, 1" is not reparsed mid-typing.
  const [daysText, setDaysText] = useState("");
  const savedDays = draft?.overdueReminderDays.join(", ") ?? "";
  useEffect(() => setDaysText(savedDays), [savedDays]);

  const [showMessages, setShowMessages] = useState(false);

  return (
    <Card data-tour="payment-reminders">
      <CardHeader
        title="Payment reminders"
        description="Emails your client about sent, unpaid invoices, with the same Pay online link as the invoice. Sent each morning, US time. They stop when the invoice is paid."
      />
      <CardBody className="space-y-5">
        {loading || !draft ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CheckboxField
                  checked={draft.reminderBeforeDueEnabled}
                  onCheckedChange={(reminderBeforeDueEnabled) =>
                    patch({ reminderBeforeDueEnabled })
                  }
                  label="Before the due date"
                  description="A heads-up that payment is coming due."
                />
                <span className="flex items-center gap-2 text-sm text-muted">
                  <NumberInput
                    className="w-16"
                    size="sm"
                    min={1}
                    aria-label="Days before due"
                    value={draft.reminderBeforeDueDays}
                    onChange={(v) =>
                      patch({ reminderBeforeDueDays: Math.max(1, v ?? 1) })
                    }
                  />
                  days before
                </span>
              </div>

              <CheckboxField
                checked={draft.reminderOnDueEnabled}
                onCheckedChange={(reminderOnDueEnabled) =>
                  patch({ reminderOnDueEnabled })
                }
                label="On the due date"
              />

              <div className="flex flex-wrap items-center justify-between gap-3">
                <CheckboxField
                  checked={draft.overdueRemindersEnabled}
                  onCheckedChange={(overdueRemindersEnabled) =>
                    patch({ overdueRemindersEnabled })
                  }
                  label="When overdue"
                  description="One email on each day you list, counted from the due date."
                />
                <span className="flex items-center gap-2 text-sm text-muted">
                  <Input
                    className="h-8 w-32"
                    aria-label="Days after due"
                    value={daysText}
                    placeholder="7, 14, 30"
                    onChange={(e) => setDaysText(e.target.value)}
                    onBlur={() => {
                      const days = parseDays(daysText);
                      setDaysText(days.join(", "));
                      if (days.join() !== draft.overdueReminderDays.join()) {
                        patch({ overdueReminderDays: days });
                      }
                    }}
                  />
                  days after
                </span>
              </div>
            </div>

            <div className="border-t border-[var(--line)] pt-4">
              <button
                type="button"
                className="text-sm font-medium text-[var(--accent)] hover:underline"
                onClick={() => setShowMessages((v) => !v)}
              >
                {showMessages ? "Hide messages" : "Edit the messages"}
              </button>
              {showMessages ? (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <Field
                    label="Before and on the due date"
                    hint="Leave empty to use this text."
                  >
                    <Textarea
                      className="min-h-44"
                      value={draft.beforeDueEmailTemplate ?? ""}
                      placeholder={DEFAULT_BEFORE_DUE}
                      onChange={(e) =>
                        patch({ beforeDueEmailTemplate: e.target.value || null })
                      }
                    />
                  </Field>
                  <Field label="Overdue" hint="Leave empty to use this text.">
                    <Textarea
                      className="min-h-44"
                      value={draft.overdueEmailTemplate ?? ""}
                      placeholder={DEFAULT_OVERDUE}
                      onChange={(e) =>
                        patch({ overdueEmailTemplate: e.target.value || null })
                      }
                    />
                  </Field>
                  <p className="text-sm text-muted lg:col-span-2">
                    Placeholders: {"{{billableName}}"}, {"{{invoiceNumber}}"},{" "}
                    {"{{totalAmount}}"}, {"{{dueDate}}"}, {"{{dueWhen}}"},{" "}
                    {"{{daysOverdue}}"}, {"{{interestAmount}}"},{" "}
                    {"{{totalWithInterest}}"}, {"{{interestNote}}"},{" "}
                    {"{{companyName}}"}. The invoice summary and Pay online
                    button are added below the message.
                  </p>
                </div>
              ) : null}
            </div>

            {anyOn ? (
              <div className="border-t border-[var(--line)] pt-4">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-medium">Next 14 days</h3>
                  {dirty ? (
                    <span className="text-sm text-muted">
                      Save to update this list.
                    </span>
                  ) : null}
                </div>
                {upcoming.isPending ? (
                  <Skeleton className="h-16" />
                ) : !upcoming.data?.length ? (
                  <p className="text-sm text-muted">
                    No reminders scheduled. Only sent, unpaid invoices get
                    them.
                  </p>
                ) : (
                  <ul className="divide-y divide-[var(--line)] text-sm">
                    {upcoming.data.slice(0, 8).map((r) => (
                      <li
                        key={`${r.invoiceId}-${r.kind}-${r.offsetDays}`}
                        className="flex items-center gap-3 py-2"
                      >
                        <span className="w-24 shrink-0 tabular-nums text-muted">
                          {formatDate(r.date)}
                        </span>
                        <Link
                          to={`/invoices/${r.invoiceId}`}
                          className="font-medium hover:underline"
                        >
                          {r.invoiceNumber}
                        </Link>
                        <span className="ml-auto">
                          <Badge tone={r.kind === "overdue" ? "warning" : "neutral"}>
                            {REMINDER_LABEL[r.kind]}
                          </Badge>
                        </span>
                      </li>
                    ))}
                    {upcoming.data.length > 8 ? (
                      <li className="py-2 text-muted">
                        and {upcoming.data.length - 8} more
                      </li>
                    ) : null}
                  </ul>
                )}
              </div>
            ) : null}
          </>
        )}
      </CardBody>
      {footer}
    </Card>
  );
}

export function LateInterestCard({ draft, loading, patch, footer }: Omit<Props, "dirty">) {
  const example =
    draft && draft.interestEnabled
      ? Math.max(0, 30 - draft.interestGraceDays) *
        (draft.interestRate / 100) *
        (1000 / (draft.interestPeriod === "year" ? 365 : 30))
      : 0;
  return (
    <Card>
      <CardHeader
        title="Late interest"
        description="Added as a line on overdue invoices: the invoice page, PDF, pay page and reminders. Paying online charges it too."
      />
      <CardBody className="space-y-4">
        {loading || !draft ? (
          <Skeleton className="h-24" />
        ) : (
          <>
            <CheckboxField
              checked={draft.interestEnabled}
              onCheckedChange={(interestEnabled) => patch({ interestEnabled })}
              label="Add late interest to overdue invoices"
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Rate (%)">
                <NumberInput
                  value={draft.interestRate}
                  onChange={(v) => patch({ interestRate: Math.min(100, v ?? 0) })}
                />
              </Field>
              <Field label="Per">
                <Select
                  className="w-full"
                  value={draft.interestPeriod}
                  onChange={(v) =>
                    patch({ interestPeriod: v === "year" ? "year" : "month" })
                  }
                  options={[
                    { value: "month", label: "Month" },
                    { value: "year", label: "Year" },
                  ]}
                />
              </Field>
              <Field label="Grace period (days)" hint="Interest starts after this.">
                <NumberInput
                  value={draft.interestGraceDays}
                  onChange={(v) => patch({ interestGraceDays: v ?? 0 })}
                />
              </Field>
            </div>
            {draft.interestEnabled ? (
              <p className="text-sm text-muted">
                A {formatMoney(1000)} invoice paid 30 days late would owe{" "}
                {formatMoney(Math.round(example * 100) / 100)} in interest.
              </p>
            ) : null}
          </>
        )}
      </CardBody>
      {footer}
    </Card>
  );
}
